import { type FormEvent, type JSX, useState } from 'react';

import { suchen, type Buchungssuchfelder, type Buchungstreffer } from '../api/buchungen';
import { ApiFehler } from '../api/fehler';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die Suchmaske für Buchungen (BUC-80, M-07; Auftraggeber, 19.09.2026).
 *
 * „Bei Buchung suchen soll sich eine Suchmaske öffnen, in der über Nummer,
 * Namen und andere Merkmale gesucht werden kann."
 *
 * BENANNTE FELDER, DIE SICH VERENGEN — wie im Suchfenster (M-07). Der Name
 * sucht Kunde UND Teilnehmer: Wer anruft, nennt mal den einen, mal den
 * anderen, und weiß selbst nicht, wer in der Buchung als Kunde steht.
 *
 * GESUCHT WIRD AUF KNOPFDRUCK und nicht bei jedem Zeichen. Die Felder
 * werden am Telefon nacheinander gefüllt; eine Liste, die nach jedem
 * Buchstaben springt, zeigt Zwischenstände einer Frage, die noch nicht
 * gestellt ist.
 */

export const STATUSWAHL: readonly { readonly wert: string; readonly text: string }[] = [
  { wert: '', text: 'jeder' },
  { wert: 'gebucht', text: 'gebucht' },
  { wert: 'umgebucht', text: 'umgebucht' },
  { wert: 'teilstorniert', text: 'teilstorniert' },
  { wert: 'storniert', text: 'storniert' },
  { wert: 'abgesagt', text: 'abgesagt' },
];

/** Der Stand einer Buchung in den Worten der Maske (DAT-11, BUC-45). */
export const STANDNAME: Readonly<Record<string, string>> = {
  gebucht: 'gebucht',
  umgebucht: 'umgebucht',
  teilstorniert: 'teilweise storniert',
  storniert: 'storniert',
  abgesagt: 'vom Haus abgesagt',
};

export const zeitpunkt = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});

export function Buchungssuche({
  nummer: vorbelegt,
  beiWahl,
  beiSchliessen,
}: {
  /** Was schon im Nummernfeld der Maske steht — es wird mitgenommen. */
  readonly nummer?: string;
  readonly beiWahl: (buchungsnummer: string) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [felder, setzeFelder] = useState<Required<Buchungssuchfelder>>({
    nummer: vorbelegt ?? '',
    name: '',
    produkt: '',
    von: '',
    bis: '',
    status: '',
  });
  const [treffer, setzeTreffer] = useState<readonly Buchungstreffer[] | null>(null);
  const [mehr, setzeMehr] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  const setze = (schluessel: keyof Buchungssuchfelder, wert: string): void => {
    setzeFelder((alt) => ({ ...alt, [schluessel]: wert }));
  };

  const leer = Object.values(felder).every((w) => w.trim() === '');

  const los = async (ereignis: FormEvent): Promise<void> => {
    ereignis.preventDefault();
    if (leer) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const antwort = await suchen(felder);
      setzeTreffer(antwort.treffer);
      setzeMehr(antwort.mehrAlsGezeigt);
    } catch (f) {
      setzeTreffer(null);
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <Dialog
      titel="Buchung suchen"
      unterzeile="Die Felder ergänzen sich — jedes weitere verengt"
      breite="breit"
      beiSchliessen={beiSchliessen}
    >
      <form onSubmit={(e) => void los(e)}>
        <div className="suchmaske__felder">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bs-nummer">
              Buchungs- oder Auftragsnummer
            </label>
            <input
              id="bs-nummer"
              className="feld__eingabe"
              value={felder.nummer}
              onChange={(e) => setze('nummer', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bs-name">
              Name (Kunde oder Teilnehmer)
            </label>
            <input
              id="bs-name"
              className="feld__eingabe"
              value={felder.name}
              placeholder="auch Kundennummer oder E-Mail"
              onChange={(e) => setze('name', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bs-produkt">
              Produkt
            </label>
            <input
              id="bs-produkt"
              className="feld__eingabe"
              value={felder.produkt}
              placeholder="Bezeichnung oder Artikelnummer"
              onChange={(e) => setze('produkt', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bs-von">
              Termin ab
            </label>
            <input
              id="bs-von"
              type="date"
              className="feld__eingabe"
              value={felder.von}
              onChange={(e) => setze('von', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bs-bis">
              Termin bis
            </label>
            <input
              id="bs-bis"
              type="date"
              className="feld__eingabe"
              value={felder.bis}
              onChange={(e) => setze('bis', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bs-status">
              Status
            </label>
            <select
              id="bs-status"
              className="feld__eingabe"
              value={felder.status}
              onChange={(e) => setze('status', e.target.value)}
            >
              {STATUSWAHL.map((s) => (
                <option key={s.wert} value={s.wert}>
                  {s.text}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="feld__hilfe">
          Die Nummer wird wörtlich gesucht, ab zwei Zeichen — auch ein Teil davon. Name und Produkt
          suchen ab drei Zeichen unscharf: „Mueller" findet auch „Müller".
        </p>
        <div className="knopfreihe">
          <button type="submit" className="knopf knopf--haupt" disabled={laeuft || leer}>
            Suchen
          </button>
        </div>
      </form>

      {fehler !== null && <Meldung fehler={fehler} />}

      {treffer !== null && treffer.length === 0 && (
        <p className="liste__leer">Keine Buchung zu diesen Angaben. Leeren Sie ein Feld — jedes erweitert die Suche.</p>
      )}
      {treffer !== null && treffer.length > 0 && (
        <>
          <p className="feld__hilfe" aria-live="polite">
            {mehr
              ? `Die ersten ${String(treffer.length)} Treffer — verengen Sie die Suche.`
              : treffer.length === 1
                ? '1 Treffer'
                : `${String(treffer.length)} Treffer`}
          </p>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Gefundene Buchungen">
              <thead>
                <tr>
                  <th scope="col">Buchung</th>
                  <th scope="col">Kunde</th>
                  <th scope="col">Produkt und Termin</th>
                  <th scope="col">Teilnehmer</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {treffer.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--verweis liste__nummer"
                        onClick={() => beiWahl(t.buchungsnummer)}
                      >
                        {t.buchungsnummer}
                      </button>
                      {t.auftragsnummer !== null && (
                        <span className="kanalliste__name"> · {t.auftragsnummer}</span>
                      )}
                    </td>
                    <td>
                      {t.kundenname}
                      <span className="kanalliste__name"> · {t.kundennummer}</span>
                    </td>
                    <td>
                      {t.produkt}
                      <br />
                      <span className="kanalliste__name liste__einzeilig">{zeitpunkt.format(new Date(t.beginn))}</span>
                    </td>
                    <td>
                      {t.teilnehmer.length === 0 ? (
                        <span className="kanalliste__name">noch keine Namen</span>
                      ) : (
                        t.teilnehmer.join(', ')
                      )}
                      <span className="kanalliste__name"> ({String(t.plaetze)} Pl.)</span>
                    </td>
                    <td>{STANDNAME[t.status] ?? t.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Dialog>
  );
}
